import { useEffect, useRef, useState, useCallback } from "react";
import { useMemo } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import {
  CaretLeft,
  PaperPlaneRight,
  Megaphone,
  Paperclip,
  X,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { ROLES } from "@/lib/roles";
import {
  getConversation,
  markConversationRead,
  sendMessage,
  subscribeToConversationMessages,
  subscribeToConversationTyping,
  uploadMessageAttachments,
  ATTACHMENT_ACCEPT,
  ATTACHMENT_MAX_BYTES,
  ATTACHMENT_MAX_PER_MESSAGE,
} from "@/lib/messaging";
import { notifyNewMessage } from "@/lib/notifications";
import { supabase, isSupabaseConfigured } from "@/lib/supabase";
import MessageBubble from "@/components/messaging/MessageBubble";
import MentionSuggest from "@/components/messaging/MentionSuggest";
import {
  buildMentionToken,
  detectMentionTrigger,
} from "@/lib/mentions";
import {
  conversationTitle,
  metaForType,
  shortTime,
} from "@/lib/messagingTypes";
import { DEMO_CLASSROOMS } from "@/lib/dailyReports";

const FAMILY_NAMES = {
  "00000000-0000-0000-0000-000000000201": "Cohen Family",
  "00000000-0000-0000-0000-000000000202": "Patel Family",
  "00000000-0000-0000-0000-000000000203": "Miller Family",
  "00000000-0000-0000-0000-000000000204": "Ndiaye Family",
  "00000000-0000-0000-0000-000000000205": "Brown Family",
};

// Demo author labels — keeps bubbles readable even when an id is opaque.
const AUTHOR_LABELS = {
  "demo-teacher": "Ms. Amara",
  "demo-parent": "Rivka",
  "demo-admin": "Priya (Admin)",
  "demo-cms": "Luca (CMS)",
};

export default function MessagingThread({ basePath }) {
  const { conversationId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const { profile, role } = useAuth();
  const [conv, setConv] = useState(null);
  const [loading, setLoading] = useState(true);
  const [body, setBody] = useState("");
  const [sending, setSending] = useState(false);
  const [pending, setPending] = useState([]);      // File[] staged for upload
  const [typingUsers, setTypingUsers] = useState([]);  // others currently typing
  const [participantDir, setParticipantDir] = useState([]);  // [{profile_id, full_name, email}] for @suggest
  const [mention, setMention] = useState(null);  // { query, start } while typing an @ token
  const endRef = useRef(null);
  const didInitialScrollRef = useRef(false);
  const fileInputRef = useRef(null);
  const textareaRef = useRef(null);
  const typingBroadcastRef = useRef(() => {});
  const lastBroadcastAtRef = useRef(0);
  const participantNameById = useMemo(
    () => Object.fromEntries(
      participantDir.map((participant) => [participant.profile_id, participant.full_name]),
    ),
    [participantDir],
  );

  /**
   * Mark the conversation read — but only when the tab is actually
   * visible. A message delivered while the user is in another tab
   * shouldn't silently flip to "read" before they've seen it; we defer
   * and re-try on the next visibilitychange.
   */
  const markReadIfVisible = useCallback(() => {
    if (!profile?.id || !conversationId) return;
    if (typeof document === "undefined" || document.visibilityState === "visible") {
      markConversationRead({ conversationId, profileId: profile.id }).catch(() => {});
    }
  }, [conversationId, profile?.id]);

  // One-shot prefill from ?prefill=<encoded-body>. Used by deep-links
  // like the "Coach this teacher" shortcut on photo moderation. Clears
  // the query param after applying so a reload doesn't re-stuff the
  // composer. If the composer already has content (edit-in-progress),
  // we respect that and skip.
  useEffect(() => {
    const raw = searchParams.get("prefill");
    if (!raw || body) return;
    try {
      const decoded = decodeURIComponent(raw);
      setBody(decoded);
      requestAnimationFrame(() => {
        const ta = textareaRef.current;
        if (!ta) return;
        ta.focus();
        try { ta.setSelectionRange(decoded.length, decoded.length); } catch { /* ignore */ }
      });
    } finally {
      const next = new URLSearchParams(searchParams);
      next.delete("prefill");
      setSearchParams(next, { replace: true });
    }
  }, [searchParams, setSearchParams, body]);

  const load = async () => {
    setLoading(true);
    try {
      const c = await getConversation({
        conversationId,
        profileId: profile.id,
      });
      setConv(c);
      if (c) {
        markReadIfVisible();
      }
    } catch (err) {
      toast.error(err.message || "Could not load thread");
    } finally {
      setLoading(false);
    }
  };

  /**
   * Hydrate the @mention suggestion directory. Own effect keyed on the
   * conversation id so a profile-fetch or RPC failure never fails the
   * primary thread load. Uses rpc_list_conversation_participants
   * (SECURITY DEFINER) instead of SELECTing conversation_participants
   * directly — the table's RLS is intentionally restrictive (avoids the
   * infinite-recursion pattern closed in Module 13), so the RPC is the
   * sanctioned path for cross-member visibility.
   */
  useEffect(() => {
    if (!isSupabaseConfigured || !conversationId) return;
    let alive = true;
    (async () => {
      try {
        const { data, error } = await supabase.rpc(
          "rpc_list_conversation_participants",
          { p_conversation_id: conversationId },
        );
        if (error) throw error;
        if (!alive) return;
        const rows = (data || []).map((r) => ({
          profile_id: r.profile_id,
          full_name: r.full_name || r.email?.split("@")[0] || "Someone",
          email: r.email,
        }));
        setParticipantDir(rows);
        if (rows.length <= 1 && process.env.NODE_ENV !== "production") {
          // Surface the "empty or self-only" case so an RLS regression
          // doesn't present as a silent empty popover again.
          // eslint-disable-next-line no-console
          console.info(
            "[mentions] participant directory hydrated with <= 1 row — check RLS / migration state.",
          );
        }
      } catch (err) {
        // eslint-disable-next-line no-console
        console.warn("[mentions] participant hydrate failed:", err?.message);
      }
    })();
    return () => { alive = false; };
  }, [conversationId]);

  useEffect(() => {
    if (!profile?.id || !conversationId) return;
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profile?.id, conversationId]);

  // Realtime: append incoming messages from other participants without
  // a full refetch. RLS still applies on the server side. For messages
  // that carry attachments, the initial INSERT payload only has the
  // messages row — we fetch the related attachments in a follow-up
  // query so the bubble shows them inline.
  useEffect(() => {
    if (!conversationId || !profile?.id) return;
    const unsub = subscribeToConversationMessages(conversationId, async (msg) => {
      let attachments = [];
      try {
        const { data } = await supabase
          .from("message_attachments")
          .select("id, file_name, mime_type, byte_size, storage_path, uploader_id, created_at")
          .eq("message_id", msg.id);
        attachments = data || [];
      } catch {
        /* non-fatal; render the text */
      }
      setConv((prev) => {
        if (!prev) return prev;
        if ((prev.messages || []).some((m) => m.id === msg.id)) return prev;
        return { ...prev, messages: [...(prev.messages || []), { ...msg, message_attachments: attachments }] };
      });
      if (msg.sender_id !== profile.id) {
        markReadIfVisible();
      }
    });
    return unsub;
  }, [conversationId, profile?.id, markReadIfVisible]);

  // Re-run mark-read when the tab becomes visible (e.g., user switched
  // back from another tab with unread messages already loaded).
  useEffect(() => {
    if (typeof document === "undefined") return;
    const onVis = () => {
      if (document.visibilityState === "visible") markReadIfVisible();
    };
    document.addEventListener("visibilitychange", onVis);
    return () => document.removeEventListener("visibilitychange", onVis);
  }, [markReadIfVisible]);

  // Typing indicators (Slice A of messaging polish). Broadcasts are
  // throttled to one per ~1.8s while the user is actively typing; the
  // receiver clears stale entries after 4.5s. Self-broadcasts are
  // filtered out by the channel config.
  useEffect(() => {
    if (!conversationId || !profile?.id) return;
    const { unsubscribe, broadcastTyping } = subscribeToConversationTyping({
      conversationId,
      profileId: profile.id,
      profileName: profile.full_name || profile.email?.split("@")[0] || "",
      onChange: setTypingUsers,
    });
    typingBroadcastRef.current = broadcastTyping;
    return () => {
      typingBroadcastRef.current = () => {};
      unsubscribe();
    };
  }, [conversationId, profile?.id, profile?.full_name, profile?.email]);

  const onBodyChange = (e) => {
    const val = e.target.value;
    const caret = e.target.selectionStart ?? val.length;
    setBody(val);

    // Mention typeahead detection — only drives UI state; no network.
    const trigger = detectMentionTrigger(val, caret);
    setMention(trigger);

    // Throttle typing broadcasts client-side so we don't flood the
    // channel on every keystroke. 1.8s < receiver's 4.5s expiry.
    const now = Date.now();
    if (now - lastBroadcastAtRef.current > 1800) {
      lastBroadcastAtRef.current = now;
      try { typingBroadcastRef.current(); } catch { /* ignore */ }
    }
  };

  /**
   * Replace the in-flight `@query` at the caret with a full mention
   * token and a trailing space, then restore focus to the textarea
   * with the caret positioned just after the inserted token.
   */
  const onMentionPick = (c) => {
    if (!mention) return;
    const ta = textareaRef.current;
    const token = buildMentionToken({ profileId: c.profile_id, name: c.full_name || c.email });
    if (!token) { setMention(null); return; }
    const before = body.slice(0, mention.start);
    const afterStart = mention.start + 1 + (mention.query || "").length; // skip '@query'
    const after = body.slice(afterStart);
    const inserted = `${token} `;
    const nextBody = `${before}${inserted}${after}`;
    setBody(nextBody);
    setMention(null);
    // Restore caret after the inserted token + space.
    requestAnimationFrame(() => {
      if (!ta) return;
      const pos = (before + inserted).length;
      ta.focus();
      try { ta.setSelectionRange(pos, pos); } catch { /* ignore */ }
    });
  };

  // Jump instantly on first paint; smooth-scroll only for new appends.
  useEffect(() => {
    if (!conv?.messages?.length) return;
    const behavior = didInitialScrollRef.current ? "smooth" : "auto";
    didInitialScrollRef.current = true;
    endRef.current?.scrollIntoView({ block: "end", behavior });
  }, [conv?.messages?.length]);

  const helpers = {
    classroomName: (id) => DEMO_CLASSROOMS.find((c) => c.id === id)?.name,
    familyName: (id) => FAMILY_NAMES[id],
  };

  // Non-admin users cannot reply to announcements in Phase 2.2.
  const canReply =
    !!conv &&
    !(conv.type === "announcement" && role !== ROLES.ADMIN);

  const onSend = async (e) => {
    e.preventDefault();
    const trimmed = body.trim();
    // A message must have EITHER text OR at least one attachment.
    if (!trimmed && pending.length === 0) return;
    setSending(true);
    try {
      // DB check constraint requires body length >= 1. If the user only
      // sent attachments, synthesize a single-character body so the
      // server doesn't 400. We strip it client-side in the bubble.
      const bodyToSend = trimmed || "📎";
      const msg = await sendMessage({
        conversationId,
        senderId: profile.id,
        body: bodyToSend,
      });

      let attachments = [];
      if (pending.length > 0) {
        try {
          attachments = await uploadMessageAttachments({
            conversationId,
            messageId: msg.id,
            organizationId: conv.organization_id || profile.organization_id,
            uploaderId: profile.id,
            files: pending.map((p) => p.file),
          });
        } catch (err) {
          // The message row was created; surface the upload error but keep
          // the message visible (the backend stored the "📎" placeholder
          // which the UI renders as empty when there are attachments,
          // and as a tiny icon if both are missing — rare).
          toast.error(err.message || "Attachment upload failed");
        }
      }

      const mergedMsg = { ...msg, message_attachments: attachments };
      setConv((prev) =>
        prev ? { ...prev, messages: [...prev.messages, mergedMsg] } : prev,
      );
      setBody("");
      setPending([]);
      if (fileInputRef.current) fileInputRef.current.value = "";

      // The backend derives recipients, content preview, mention state,
      // preferences, and center scope from the stored message. The browser
      // sends only the new durable row id, so it cannot select recipients or
      // alter the delivery context. The backend reuses a message-specific
      // idempotency key rather than a browser-built time-window key.
      if (isSupabaseConfigured) {
        notifyNewMessage(msg.id).catch(() => {
          /* messaging itself already succeeded; delivery is non-blocking */
        });
      }
    } catch (err) {
      toast.error(err.message || "Could not send");
    } finally {
      setSending(false);
    }
  };

  const onPickFiles = (e) => {
    const picked = Array.from(e.target.files || []);
    if (picked.length === 0) return;
    // Enforce slot + size + MIME caps before staging so we fail fast.
    if (pending.length + picked.length > ATTACHMENT_MAX_PER_MESSAGE) {
      toast.error(`Up to ${ATTACHMENT_MAX_PER_MESSAGE} files per message.`);
      e.target.value = "";
      return;
    }
    const bad = picked.find((f) => f.size > ATTACHMENT_MAX_BYTES);
    if (bad) {
      toast.error(`"${bad.name}" is larger than 25 MB.`);
      e.target.value = "";
      return;
    }
    // Wrap each File in a stable-keyed pending record so two files with
    // the same name don't collide on React's list-reconciliation key.
    const wrapped = picked.map((file) => ({
      key: (typeof crypto !== "undefined" && crypto.randomUUID)
        ? crypto.randomUUID()
        : `${file.name}-${Date.now()}-${Math.random()}`,
      file,
    }));
    setPending((prev) => [...prev, ...wrapped]);
    e.target.value = "";
  };

  const removePending = (key) => {
    setPending((prev) => prev.filter((p) => p.key !== key));
  };

  if (loading) {
    return (
      <div
        data-testid="thread-loading"
        className="flex items-center justify-center py-16"
      >
        <div className="h-8 w-8 rounded-full border-2 border-primary/30 border-t-primary animate-spin" />
      </div>
    );
  }

  if (!conv) {
    return (
      <div className="py-16 text-center text-sm text-neutral-500">
        Conversation not found.
      </div>
    );
  }

  const meta = metaForType(conv.type);
  const Icon = meta.icon;
  const title = conversationTitle(conv, helpers);

  return (
    <div
      data-testid="messaging-thread"
      data-conversation-type={conv.type}
      className="-mb-[calc(6.5rem+env(safe-area-inset-bottom,0px))] flex h-[calc(100dvh-10.5rem-env(safe-area-inset-bottom,0px))] flex-col gap-3 sm:mb-0 sm:h-auto sm:gap-5"
    >
      {/* Compact chat header: back chevron folded in, quiet meta line. */}
      <header className="flex shrink-0 items-center gap-2 rounded-2xl border border-neutral-200 bg-white py-2.5 pl-2 pr-4">
        <Link
          to={basePath}
          data-testid="thread-back"
          aria-label="Back to inbox"
          className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-neutral-500 transition hover:bg-neutral-100 hover:text-neutral-900"
        >
          <CaretLeft size={18} weight="bold" />
          <span className="sr-only">Back</span>
        </Link>
        <div className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-lg ${meta.bg}`}>
          <Icon size={18} weight="duotone" className={meta.color} />
        </div>
        <div className="min-w-0 flex-1">
          <h1 className="truncate font-heading text-[15px] font-semibold tracking-tight text-neutral-900">
            {title}
          </h1>
          <p className="truncate text-[11px] text-neutral-500">
            {meta.label} · Started {shortTime(conv.created_at)}
          </p>
        </div>
      </header>

      {conv.type === "announcement" && role !== ROLES.ADMIN && (
        <div
          data-testid="thread-announcement-notice"
          className="flex shrink-0 items-start gap-3 rounded-2xl border border-primary/30 bg-primary/[0.06] p-4 text-sm text-neutral-800"
        >
          <Megaphone size={18} weight="duotone" className="mt-0.5 text-primary-hover" />
          <p>
            This is an announcement. Replies are disabled — if you need to
            respond, please start a new conversation with your teacher.
          </p>
        </div>
      )}

      <ul
        className="flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto overscroll-contain pr-0.5"
        data-testid="thread-messages"
      >
        {/* Bottom-anchors short threads without the column-reverse bugs. */}
        <li aria-hidden className="mt-auto" />
        {conv.messages.length === 0 ? (
          <li className="my-auto py-6 text-center text-sm text-neutral-500">
            No messages yet.
          </li>
        ) : (
          conv.messages.map((m) => (
            <li key={m.id}>
              <MessageBubble
                message={m}
                isMine={m.sender_id === profile.id}
                authorName={
                  participantNameById[m.sender_id]
                    || AUTHOR_LABELS[m.sender_id]
                    || "Someone"
                }
                viewerProfileId={profile.id}
              />
            </li>
          ))
        )}
        <li ref={endRef} />
      </ul>

      {typingUsers.length > 0 && (
        <div
          data-testid="thread-typing-indicator"
          aria-live="polite"
          className="-mt-1 flex shrink-0 items-center gap-2 px-1 text-xs text-neutral-500"
        >
          <span className="inline-flex items-end gap-0.5" aria-hidden>
            <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-neutral-400 [animation-delay:-240ms]" />
            <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-neutral-400 [animation-delay:-120ms]" />
            <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-neutral-400" />
          </span>
          <span>
            {typingLabel(typingUsers)}
          </span>
        </div>
      )}

      {canReply && (
        <form
          onSubmit={onSend}
          data-testid="thread-composer"
          className="mb-2 flex shrink-0 flex-col gap-2 rounded-2xl border border-neutral-200 bg-white p-3 shadow-raised sm:mb-0 sm:sticky sm:bottom-[calc(6.5rem+env(safe-area-inset-bottom,0px))] sm:z-30"
        >
          <div className="relative">
            <MentionSuggest
              open={!!mention}
              query={mention?.query || ""}
              candidates={participantDir}
              selfId={profile.id}
              onSelect={onMentionPick}
              onClose={() => setMention(null)}
            />
          </div>
          {pending.length > 0 && (
            <ul
              data-testid="thread-pending-attachments"
              className="flex flex-wrap gap-2"
            >
              {pending.map((p) => (
                <li
                  key={p.key}
                  className="flex items-center gap-1.5 rounded-full border border-neutral-200 bg-neutral-50 py-1 pl-2 pr-1 text-xs text-neutral-700"
                >
                  <Paperclip size={11} weight="duotone" className="text-neutral-500" />
                  <span className="max-w-[180px] truncate font-medium">{p.file.name}</span>
                  <span className="text-[10px] text-neutral-500">{formatSize(p.file.size)}</span>
                  <button
                    type="button"
                    onClick={() => removePending(p.key)}
                    aria-label={`Remove ${p.file.name}`}
                    data-testid="thread-pending-remove"
                    disabled={sending}
                    className="ml-1 flex h-5 w-5 items-center justify-center rounded-full text-neutral-400 hover:bg-neutral-200 hover:text-neutral-700"
                  >
                    <X size={10} weight="bold" />
                  </button>
                </li>
              ))}
            </ul>
          )}
          <div className="flex items-end gap-2">
            <input
              ref={fileInputRef}
              type="file"
              multiple
              hidden
              data-testid="thread-file-input"
              accept={ATTACHMENT_ACCEPT.join(",")}
              onChange={onPickFiles}
            />
            <button
              type="button"
              data-testid="thread-attach"
              disabled={sending || pending.length >= ATTACHMENT_MAX_PER_MESSAGE}
              onClick={() => fileInputRef.current?.click()}
              aria-label="Attach files"
              className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full border border-neutral-200 bg-white text-neutral-600 transition hover:bg-neutral-50 disabled:cursor-not-allowed disabled:opacity-50"
            >
              <Paperclip size={16} weight="duotone" />
            </button>
            <textarea
              ref={textareaRef}
              data-testid="thread-input"
              rows={1}
              value={body}
              onChange={onBodyChange}
              onKeyDown={(e) => {
                // When the mention popover is open, let it own
                // ArrowUp/Down/Enter/Tab/Escape — its own keydown
                // listener (bound with capture=true) handles those.
                if (mention && ["ArrowDown", "ArrowUp", "Enter", "Tab", "Escape"].includes(e.key)) {
                  return;
                }
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  onSend(e);
                }
              }}
              placeholder="Write a reply…"
              className="min-h-10 max-h-32 flex-1 resize-none rounded-xl border border-transparent bg-neutral-50 px-3 py-2.5 text-sm text-neutral-900 focus:border-primary focus:bg-white focus:outline-none focus:ring-2 focus:ring-primary/20"
            />
            <button
              type="submit"
              data-testid="thread-send"
              disabled={sending || (body.trim().length === 0 && pending.length === 0)}
              className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-primary text-white transition-all hover:bg-primary-hover active:scale-[0.95] disabled:cursor-not-allowed disabled:opacity-50"
              aria-label="Send"
            >
              <PaperPlaneRight size={16} weight="fill" />
            </button>
          </div>
        </form>
      )}
    </div>
  );
}

function formatSize(bytes) {
  if (!bytes || bytes < 0) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/**
 * "Priya is typing…" / "Priya and Amara are typing…" / "3 people are
 * typing…" — capped at 2 names to keep the pill short on mobile.
 */
function typingLabel(entries) {
  if (!entries || entries.length === 0) return "";
  const names = entries
    .map((e) => e.name?.trim() || "Someone")
    .filter(Boolean);
  if (names.length === 1) return `${names[0]} is typing…`;
  if (names.length === 2) return `${names[0]} and ${names[1]} are typing…`;
  return `${names.length} people are typing…`;
}
